<script lang="ts">
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Wrench, ChevronDown } from 'lucide-svelte';
  import { companiesApi, dealsApi } from '$lib/api/crm';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { blocksOrderClosure } from '$lib/utils/orders';
  import TcsNumber from '../ui/TcsNumber.svelte';
  import { SITE_STATUS_FLAG_LABELS } from '$lib/types';
  import type { DealSite, HotelSite, SitePhase } from '$lib/types';

  // Grouped site-card grid (Customers / Contacting / collapsed Closed),
  // shared by SitesPage (a vertical's whole site list) and CompanyDetail's
  // owner rollup (one owner's sites) — extracted verbatim from SitesPage so
  // both stay pixel-identical without a second copy of the grouping/badge
  // logic.
  const {
    rows,
    isHotels,
    onOpen,
    showOwner = true,
    showBrand = true
  }: {
    rows: (HotelSite | DealSite)[];
    isHotels: boolean;
    onOpen: (id: number) => void;
    // Owner/brand attribution line is redundant on a single-owner page
    // (e.g. OwnerDashboard) — both default true so SitesPage, which lists
    // sites across many owners, is unaffected.
    showOwner?: boolean;
    showBrand?: boolean;
  } = $props();

  const queryClient = useQueryClient();

  const PHASE_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
    '': { bg: '#F1F0EF', fg: '#666666', label: 'Not set' },
    contacting: { bg: '#E6E9EE', fg: '#10223A', label: 'Contacting' },
    mockup: { bg: '#FBF0DC', fg: '#8a5c00', label: 'Mockup' },
    material_supplied: { bg: '#FBF0DC', fg: '#8a5c00', label: 'Material supplied' },
    installed: { bg: '#E3F0F1', fg: '#226A73', label: 'Installed' },
    maintaining: { bg: '#EAE6F3', fg: '#5a4b8a', label: 'Maintaining' },
    closed: { bg: '#ECF8CC', fg: '#5c7a00', label: 'Closed' }
  };
  // Same values/order as PHASE_BADGE, reused for the inline <select> options.
  const PHASE_OPTIONS: Array<{ value: SitePhase; label: string }> = [
    { value: '', label: 'Not set' },
    { value: 'contacting', label: 'Contacting' },
    { value: 'mockup', label: 'Mockup' },
    { value: 'material_supplied', label: 'Material supplied' },
    { value: 'installed', label: 'Installed' },
    { value: 'maintaining', label: 'Maintaining' },
    { value: 'closed', label: 'Closed' }
  ];

  // Inline phase edit, straight from the card — same write path as
  // SiteDashboard's status strip (companiesApi vs dealsApi.update per
  // isHotels), just without opening the full dashboard first. Invalidate
  // (not optimistic): the grouping this card lives in is derived from
  // `rows`, a prop owned by the parent's own query, so a refetch is what
  // actually moves the card between sections.
  const phaseMutation = createMutation(() => ({
    mutationFn: async ({ id, phase }: { id: number; phase: SitePhase }) => {
      return isHotels ? companiesApi.update(id, { site_phase: phase }) : dealsApi.update(id, { site_phase: phase });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['company-rollup'] });
      queryClient.invalidateQueries({ queryKey: ['site-detail'] });
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function handlePhaseChange(row: HotelSite | DealSite, e: Event) {
    e.stopPropagation();
    const phase = (e.currentTarget as HTMLSelectElement).value as SitePhase;
    if (!isHotels && blocksOrderClosure(phase, row as DealSite)) {
      (e.currentTarget as HTMLSelectElement).value = row.site_phase;
      toast.error('Cannot close an installed order until feedback is submitted and confirmed by support. OFF-mode feedback may be unverified.'); return;
    }
    phaseMutation.mutate({ id: row.id, phase });
  }

  // "Customers" vs "Contacting" grouping (client-side only, no API change —
  // see the sites revamp phase 1 spec): a site counts as a customer once
  // it's past initial contact, unset/contacting sites are still being
  // chased, and closed sites get their own collapsed-by-default section.
  const CUSTOMER_PHASES = new Set<SitePhase>(['mockup', 'material_supplied', 'installed', 'maintaining']);

  let closedOpen = $state(false);

  const customerRows = $derived(rows.filter((r) => CUSTOMER_PHASES.has(r.site_phase)));
  const contactingRows = $derived(rows.filter((r) => r.site_phase === '' || r.site_phase === 'contacting'));
  const closedRows = $derived(rows.filter((r) => r.site_phase === 'closed'));

  function isHotelSite(row: HotelSite | DealSite): row is HotelSite {
    return isHotels;
  }

  function flagBadges(row: HotelSite | DealSite): string[] {
    const flags = row.site_status.flags ?? {};
    return Object.keys(flags)
      .filter((k) => flags[k])
      .map((k) => SITE_STATUS_FLAG_LABELS[k] ?? k);
  }
</script>

{#if rows.length === 0}
  <div class="tym-card flex flex-col items-center justify-center px-6 py-12 text-center">
    <div class="font-heading text-base font-bold text-navy">No sites match these filters</div>
  </div>
{:else}
  {#snippet siteCard(row: HotelSite | DealSite)}
    {@const phaseBadge = PHASE_BADGE[row.site_phase] ?? PHASE_BADGE['']}
    {@const flags = flagBadges(row)}
    <div
      class="tym-card flex cursor-pointer flex-col gap-2.5 px-4 py-3.5 text-left"
      role="button"
      tabindex="0"
      onclick={() => onOpen(row.id)}
      onkeydown={(e) => e.key === 'Enter' && onOpen(row.id)}
    >
      <div class="flex flex-col items-start gap-1.5">
        <span class="line-clamp-2 font-heading text-[14.5px] font-bold leading-snug text-navy" title={row.name}
          >{row.name}</span
        >
        <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
        <select
          class="tym-badge max-w-full shrink-0 cursor-pointer border-0"
          style="background:{phaseBadge.bg}; color:{phaseBadge.fg}"
          value={row.site_phase}
          onclick={(e) => e.stopPropagation()}
          onchange={(e) => handlePhaseChange(row, e)}
          disabled={phaseMutation.isPending}
          title="Set phase"
        >
          {#each PHASE_OPTIONS as opt (opt.value)}
            <option value={opt.value}>{opt.label}</option>
          {/each}
        </select>
      </div>
      {#if isHotelSite(row)}
        {@const brandText = showBrand ? row.brand_name : null}
        {@const ownerText = showOwner ? row.owner_name : null}
        {#if showBrand || showOwner}
          <div class="text-[12px] text-dark-gray">
            {#if brandText}{brandText}{/if}
            {#if brandText && ownerText} &middot; {/if}
            {#if ownerText}{ownerText}{/if}
            {#if !brandText && !ownerText}—{/if}
          </div>
        {/if}
      {:else}
        {#if row.order_number}<TcsNumber value={row.order_number} />{/if}
        <div class="text-[12px] text-dark-gray">{row.company_name ?? '—'} &middot; <span class="capitalize">{row.stage || '—'}</span></div>
      {/if}
      {#if flags.length}
        <div class="flex flex-wrap gap-1.5">
          {#each flags as f (f)}
            <span class="tym-badge" style="background:#FBEAEA; color:#9c3b3b">{f}</span>
          {/each}
        </div>
      {/if}
      <div class="mt-auto flex items-center gap-3 pt-1 text-[11.5px] text-dark-gray">
        <span class="inline-flex items-center gap-1"><Wrench class="h-3 w-3" />{row.open_issues} open</span>
        {#if isHotelSite(row)}
          <span>{row.deals.length} deal{row.deals.length === 1 ? '' : 's'}</span>
        {/if}
      </div>
    </div>
  {/snippet}

  {#if customerRows.length}
    <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-toyama-teal">
      Customers ({customerRows.length})
    </div>
    <div class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {#each customerRows as row (row.id)}
        {@render siteCard(row)}
      {/each}
    </div>
  {/if}

  {#if contactingRows.length}
    <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">
      Contacting ({contactingRows.length})
    </div>
    <div class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {#each contactingRows as row (row.id)}
        {@render siteCard(row)}
      {/each}
    </div>
  {/if}

  {#if closedRows.length}
    <div class="tym-card mb-5 px-4 py-3">
      <button type="button" class="flex w-full items-center gap-2 text-left" onclick={() => (closedOpen = !closedOpen)}>
        <span class="flex-1 text-[13px] font-semibold text-navy">Closed ({closedRows.length})</span>
        <ChevronDown class="h-4 w-4 shrink-0 text-dark-gray transition-transform {closedOpen ? 'rotate-180' : ''}" />
      </button>
      {#if closedOpen}
        <div class="mt-3 grid grid-cols-1 gap-3 border-t border-toyama-cloudy-gray pt-3 sm:grid-cols-2 lg:grid-cols-3">
          {#each closedRows as row (row.id)}
            {@render siteCard(row)}
          {/each}
        </div>
      {/if}
    </div>
  {/if}
{/if}
